/**
 * Web chat tool-card for `novaList` + `novaRunHistory` (M4.3):
 *
 * - novaList        → the automation board (name + status dot).
 * - novaRunHistory  → recent runs (status + relative time).
 *
 * Web-only (a `.native.tsx` sibling renders iOS/Android). Copy from `nova.card.*`.
 */
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { NovaAutomationSummary, NovaRunSummary } from '@tabbio/types';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { asArray, safeArray, safeGet } from './types';
import type { ToolCardProps } from './types';
import { TaskRows } from '../task-rows';
import { novaRunsToTaskRows } from '../task-rows-model';

const MAX_DISPLAY = 8;

const STATUS_DOT: Record<string, string> = {
  active: 'var(--success, #1a7f37)',
  succeeded: 'var(--success, #1a7f37)',
  failed: 'var(--danger, #b3261e)',
  killed: 'var(--danger, #b3261e)',
  running: '#5e29ff',
  awaiting_approval: '#b26a00',
};

export const NovaListCard: FC<ToolCardProps> = ({ toolName, result }) => {
  const { t } = useTranslation('common');
  const isRuns = toolName === 'novaRunHistory';

  const items = isRuns
    ? pick<NovaRunSummary>(result, 'runs')
    : pick<NovaAutomationSummary>(result, 'automations');
  const total = safeGet<number>(result, 'total') ?? items.length;
  const shown = items.slice(0, MAX_DISPLAY);

  const label = isRuns ? t('nova.card.runsLabel') : t('nova.card.listLabel');
  const emptyLabel = isRuns ? t('nova.card.runsEmpty') : t('nova.card.listEmpty');

  // Runs are units of work with a status, which is exactly what a task row is:
  // succeeded → check, failed → cross, running → live ring, with the run's
  // timings and error behind the chevron.
  if (isRuns && shown.length > 0) {
    return (
      <CardShell>
        <div style={{ padding: '14px 18px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol name="history" size={15} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>{label}</span>
        </div>
        <TaskRows rows={novaRunsToTaskRows(shown as NovaRunSummary[], t)} variant="list" />
        <div style={{ padding: '8px 18px 14px', fontSize: 12, color: 'var(--text-tertiary)' }}>
          {t('nova.card.runsCount', { count: total })}
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div style={{ padding: '16px 18px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol name={isRuns ? 'history' : 'conversion_path'} size={15} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>{label}</span>
        </div>

        {shown.length === 0 ? (
          <div style={{ paddingBottom: 8, fontSize: 13, color: 'var(--text-tertiary)' }}>{emptyLabel}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {shown.map((item, index) => {
              const status = String(safeGet<string>(item, 'status') ?? '');
              const primary = isRuns
                ? t(`nova.runs.runStatus.${status}`, { defaultValue: status })
                : (safeGet<string>(item, 'name') ?? 'N/A');
              const secondary = isRuns
                ? (safeGet<string>(item, 'errorSummary') ?? undefined)
                : t(`nova.status.${status}`, { defaultValue: status });
              return (
                <div
                  key={safeGet<string>(item, 'id') ?? index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 0',
                    borderTop: index > 0 ? '1px solid var(--input-border)' : 'none',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: STATUS_DOT[status] ?? 'var(--text-tertiary)',
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    dir="auto"
                  >
                    {primary}
                  </span>
                  {secondary ? (
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }}>{secondary}</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ padding: '8px 18px 14px', fontSize: 12, color: 'var(--text-tertiary)' }}>
        {isRuns
          ? t('nova.card.runsCount', { count: total })
          : t('nova.card.count', { count: total })}
      </div>
    </CardShell>
  );
};

function pick<T>(result: unknown, key: string): T[] {
  const fromKey = safeArray<T>(result, key);
  return fromKey.length > 0 ? fromKey : asArray<T>(result);
}
